import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { HealthStatus } from '@swyftx/features/loans/components/LoanHealth/api/types/loanHealth.types';
import {
  HealthConfig,
  LOAN_HEALTH_CONFIG,
} from '@swyftx/features/loans/components/SingleLoansDashboard/api/types/singleLoanHealthCard.types';

import { Loan } from '@shared/api/@types/loans';
import { Big } from '@shared/safe-big';
import { formatCurrency } from '@shared/utils';

import { useMarkets } from '../../../../../../lib/markets/hooks/useMarkets';

export const useSingleLoanHealthCard = (loanData?: Loan) => {
  const { getAssetByCode } = useMarkets();
  const { t } = useTranslation('loans');

  const securityAsset = useMemo(
    () => getAssetByCode(loanData?.securityCryptoCurrency ? loanData?.securityCryptoCurrency : 'BTC'),
    [getAssetByCode, loanData?.securityCryptoCurrency],
  );
  const fiatAsset = useMemo(
    () => getAssetByCode(loanData?.fiatCurrency ? loanData.fiatCurrency : 'AUD'),
    [getAssetByCode, loanData?.fiatCurrency],
  );

  const securityCryptoAmountFormatted = useMemo(
    () =>
      formatCurrency(Big(loanData?.securityCryptoAmount ?? 0).toFixed(8), securityAsset, {
        appendCode: true,
        showTrailingZero: true,
      }),
    [loanData?.securityCryptoAmount, securityAsset],
  );

  const securityFiatAmountFormatted = useMemo(
    () =>
      `~ ${formatCurrency(loanData?.securityFiatAmount ?? 0, fiatAsset, {
        appendCode: true,
      })}`,
    [loanData?.securityFiatAmount, fiatAsset],
  );

  const lvrPercentage = useMemo(() => {
    if (loanData?.loanToValue === undefined || loanData.loanToValue === null) {
      return undefined;
    }
    return Big(loanData.loanToValue).mul(100).toFixed(2);
  }, [loanData?.loanToValue]);

  const loanToValue24hTrendPercentage = useMemo(() => {
    if (loanData?.loanToValue24hTrend === undefined || loanData.loanToValue24hTrend === null) {
      return undefined;
    }

    const trend = Big(loanData.loanToValue24hTrend);
    const formattedValue = trend.mul(1000).toFixed(2);

    if (trend.gt(0)) {
      return `+${formattedValue}`;
    }

    return formattedValue;
  }, [loanData?.loanToValue24hTrend]);

  const { color, statusTitle } = useMemo(
    () =>
      loanData?.loanToValueStatus
        ? LOAN_HEALTH_CONFIG[loanData.loanToValueStatus]
        : LOAN_HEALTH_CONFIG[HealthStatus.Inactive],
    [loanData?.loanToValueStatus],
  );

  const trendChangeColor: HealthConfig['color'] = useMemo(
    () => (!loanData?.loanToValue24hTrend ? 'placeholder' : loanData.loanToValue24hTrend.lt(0) ? 'success' : 'error'),
    [loanData?.loanToValue24hTrend],
  );

  return {
    securityAsset: securityAsset,
    fiatAsset: fiatAsset,
    securityCryptoAmountFormatted,
    securityFiatAmountFormatted,
    lvrPercentage,
    loanToValue24hTrendPercentage,
    healthStatusColor: color,
    trendChangeColor,
    statusTitle: t(statusTitle as unknown as TemplateStringsArray),
  };
};
